import { useCallback, useMemo, useRef, useState } from 'react';

import {
  ConfirmDialog,
  TableRowActionsMenu,
  TableRowActionsMenuItem,
  Typography,
} from '@occc-app/design-system';
import { PromoCodeGroupFileType } from '@occc/graphql';

import { PromocodesIndividualDownloadDialog } from './promocodes-individual-download-dialog.component';

import { PromoCodeGroupFormValues } from '../PromocodesConfiguration/promocodes-configuration.types';
import {
  getPromoCodeGroupDownloadMenuItems,
  isPromoCodeGroupDownloadAvailable,
} from '../PromocodesConfiguration/promocodes-individual-configuration.utils';
import { usePromoCodeGroupDownloadLink } from '../../hooks/promocodes-download.query.hook';
import { messages } from '../../promocodes.messages';

type DownloadDialogState = {
  open: boolean;
  fileName: string;
  downloadUrl: string | null;
  error?: string;
};

const initialDialogState: DownloadDialogState = {
  open: false,
  fileName: '',
  downloadUrl: null,
};

const getFailedState = (prev: DownloadDialogState, fileName: string): DownloadDialogState => ({
  ...prev,
  fileName,
  downloadUrl: null,
  error: messages.downloadLinkGenerationFailed,
});

type PromocodesIndividualRowActionsProps = {
  group: PromoCodeGroupFormValues;
  disabled?: boolean;
  isDeleteEnabled?: boolean;
  onDelete: (groupId: string) => void | Promise<void>;
};

export const PromocodesIndividualRowActions = ({
  group,
  disabled,
  isDeleteEnabled,
  onDelete,
}: PromocodesIndividualRowActionsProps) => {
  const [dialogState, setDialogState] = useState<DownloadDialogState>(initialDialogState);
  const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false);
  const activeDownloadRequestRef = useRef(0);

  const [fetchDownloadLink, { loading: isFetchingDownloadLink }] = usePromoCodeGroupDownloadLink({
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    onError: () => {},
  });

  const handleDownloadRequest = useCallback(
    async (fileType: PromoCodeGroupFileType) => {
      const requestId = ++activeDownloadRequestRef.current;

      setDialogState({
        open: true,
        fileName: group.fileName,
        downloadUrl: null,
      });

      try {
        const { data } = await fetchDownloadLink({
          variables: {
            filter: {
              promoCodeGroupId: group.id,
              fileType,
            },
          },
        });

        if (requestId !== activeDownloadRequestRef.current) {
          return;
        }

        const downloadUrl = data?.promoCodeGroupDownloadLink ?? null;

        if (!downloadUrl) {
          setDialogState((prev) => getFailedState(prev, group.fileName));
          return;
        }

        setDialogState((prev) => ({
          ...prev,
          fileName: group.fileName,
          downloadUrl,
        }));
      } catch {
        if (requestId !== activeDownloadRequestRef.current) {
          return;
        }
        setDialogState((prev) => getFailedState(prev, group.fileName));
      }
    },
    [fetchDownloadLink, group.fileName, group.id],
  );

  const handleDeleteConfirm = () => {
    setConfirmationDialogOpen(false);
    onDelete(group.id);
  };

  const menuItems: TableRowActionsMenuItem[] = useMemo(() => {
    const items: TableRowActionsMenuItem[] = [];

    if (isPromoCodeGroupDownloadAvailable(group)) {
      items.push(
        ...getPromoCodeGroupDownloadMenuItems(group).map((item) => ({
          label: item.label,
          onClick: () => handleDownloadRequest(item.fileType),
        })),
      );
    }

    if (isDeleteEnabled) {
      items.push({
        label: 'Delete File',
        onClick: () => setConfirmationDialogOpen(true),
        sx: { color: 'error.main' },
      });
    }

    return items;
  }, [group, handleDownloadRequest, isDeleteEnabled]);

  const handleDialogClose = () => {
    activeDownloadRequestRef.current += 1;
    setDialogState(initialDialogState);
  };

  if (!menuItems.length) {
    return null;
  }

  const hasDownloadMenuItems = isPromoCodeGroupDownloadAvailable(group);

  return (
    <>
      <TableRowActionsMenu
        menuItems={menuItems}
        disabled={hasDownloadMenuItems ? Boolean(disabled) : false}
        divided
      />
      <PromocodesIndividualDownloadDialog
        open={dialogState.open}
        fileName={dialogState.fileName}
        downloadUrl={dialogState.downloadUrl}
        error={dialogState.error}
        loading={isFetchingDownloadLink}
        onClose={handleDialogClose}
      />
      <ConfirmDialog
        dialogTitle='Delete File?'
        open={confirmationDialogOpen}
        onClose={() => setConfirmationDialogOpen(false)}
        confirmButtonText='Delete'
        confirmButtonProps={{
          onClick: handleDeleteConfirm,
          color: 'error',
        }}
      >
        <Typography gutterBottom>This action will delete file "{group.fileName}".</Typography>
        <Typography color='text.secondary'>
          {messages.deleteIndividualPromoCodesRedeemedNote}
        </Typography>
      </ConfirmDialog>
    </>
  );
};
